import React, { useState, useEffect } from 'react';
import { Container, Tab, Tabs, Alert } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { getUserRoles } from '../services/api';
import Questionnaire from './Questionnaire';
import Reporting from './Reporting';
import DataAugmentation from './DataAugmentation';
import { getRuntimeFlag } from '../config/runtimeSettings';
import '../styles/embedded.css';

// Read feature flag from window.ENV (runtime) or process.env (build time)
const SHOW_DATA_AUGMENTATION = getRuntimeFlag('SHOW_DATA_AUGMENTATION');

const EmbeddedApp = () => {
  const { isAuthenticated, loading, error, checkAuth } = useAuth();
  const [activeTab, setActiveTab] = useState(SHOW_DATA_AUGMENTATION ? 'data-augmentation' : 'questionnaires');
  const [orgId, setOrgId] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const [hasRestrictedAccess, setHasRestrictedAccess] = useState(false);
  const [tokenSet, setTokenSet] = useState(false);

  useEffect(() => {
    window.parent.postMessage('REQUEST_CREDENTIALS', '*');

    const handleMessage = (event) => {
      const data = event.data;
      if (data.type === 'CREDENTIALS') { 
        const { token, organisationId } = data.payload;
        // Set all token variations
        localStorage.setItem('token', token);
        localStorage.setItem('access_token', token);
        localStorage.setItem('refreshToken', token);
        setOrgId(organisationId);
        localStorage.setItem('orgId', organisationId);
        
        // Mark token as set immediately
        setTokenSet(true);
        
        // Call checkAuth immediately after token is set
        // Use a small delay (100ms) to ensure localStorage is fully updated
        // and axios interceptors can pick up the token
        setTimeout(() => {
          if (typeof checkAuth === 'function') {
            checkAuth();
          }
        }, 100);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [checkAuth]);

  useEffect(() => {
    let retryCount = 0;
    const MAX_RETRIES = 3;
    
    const checkUserRoles = async () => {
      if (isAuthenticated && orgId && tokenSet) {
        try {
          const roles = await getUserRoles();
          setUserRoles(roles);
          const allowedRoles = [1, 2, 3, 4, 5, 23];
          const hasAllowedRole = roles.some(role => allowedRoles.includes(role.RoleID));
          setHasRestrictedAccess(!hasAllowedRole);
          setIsInitialized(true);
          retryCount = 0; // Reset on success
        } catch (error) {
          console.error('Error checking user roles:', error);
          
          // Don't retry on authentication errors (401) - let auth system handle it
          if (error.response?.status === 401) {
            console.warn('Authentication error when checking user roles, not retrying');
            // Don't set initialized, but don't retry either
            return;
          }
          
          // Only retry for non-auth errors and if we haven't exceeded max retries
          if (retryCount < MAX_RETRIES) {
            retryCount++;
            setTimeout(() => {
              checkUserRoles();
            }, 2000);
          } else {
            console.error('Max retries reached for getUserRoles, giving up');
            // Set initialized anyway to prevent infinite loading
            setIsInitialized(true);
          }
        }
      }
    };

    checkUserRoles();
  }, [isAuthenticated, orgId, tokenSet]);

  if (loading || !tokenSet) {
    return (
      <Container className="py-3 embedded-app">
        <div className="text-center text-muted">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Container>
    );
  }

  if (!isAuthenticated) {
    return (
      <Container className="py-3 embedded-app">
        {/* <Alert variant="warning">
          Authentication required to access the embedded application. Token Verification Failed.
        </Alert> */}
      <div className="text-center text-muted">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Initializing...</span>
        </div>
      </div>
      </Container>
    );
  }

  if (!isInitialized || !orgId) {
    return (
      <Container className="py-3 embedded-app">
        <div className="text-center text-muted">
          Initializing...
        </div>
      </Container>
    );
  }

  if (hasRestrictedAccess) {
    return (
      <Container fluid className="p-3 embedded-app">
        <Alert variant="warning" className="m-3">
          Access to Total Remuneration is not available for your role. Please contact your key accounts manager for assistance.
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="p-3 embedded-app">
      {error && (
        <Alert variant="danger" className="m-3">
          {error}
        </Alert>
      )}
      
      <Tabs
        activeKey={activeTab}
        onSelect={setActiveTab}
        className="mb-0 embedded-tabs"
      >
        {SHOW_DATA_AUGMENTATION && (
          <Tab eventKey="data-augmentation" title="LTI Data Collection" className="embedded-tab-content">
            <DataAugmentation orgId={orgId.toString()} />
          </Tab>
        )}
        <Tab eventKey="questionnaires" title="Questionnaires" className="embedded-tab-content">
          <Questionnaire orgId={orgId.toString()} />
        </Tab>
        <Tab eventKey="reporting" title="Reporting" className="embedded-tab-content">
          <Reporting orgId={orgId.toString()} />
        </Tab>
      </Tabs>
    </Container>
  );
};

export default EmbeddedApp; 